import { MultiSelect, createStyles, rem } from "@mantine/core";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useEffect, useState } from "react";
import { FiChevronLeft, FiDownload, FiPrinter } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import styled from "styled-components";
import { Unit } from "../../entities/unit";
import { Person, UnitService } from "../../services/unit/unit.service";
import { PageHeader, PageWrapper } from "./styles";

const useStyles = createStyles((theme) => ({
  root: {
    position: "relative",
    marginBottom: rem(2.4),
  },
  input: {
    height: rem(56),
    paddingTop: rem(18),
  },
  label: {
    position: "absolute",
    pointerEvents: "none",
    fontSize: theme.fontSizes.xs,
    paddingLeft: theme.spacing.sm,
    paddingTop: `calc(${theme.spacing.sm} / 2)`,
    zIndex: 1,
  },
}));

type UnitPeople = {
  unitId: string;
  unitName: string;
  people: Person[];
};

const formatDate = (iso?: string): string => {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

export function ListaMembros() {
  const { classes } = useStyles();
  const navigate = useNavigate();

  const [units, setUnits] = useState<Unit[]>([]);
  const [unitsSelected, setUnitsSelected] = useState<string[]>([]);
  const [reportData, setReportData] = useState<UnitPeople[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);

  const totalMembers = reportData.reduce((acc, u) => acc + u.people.length, 0);

  const fetchUnits = async (): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await UnitService.get();
      setUnits(response);
    } catch (error: any) {
      toast.warning(error[0], { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReport = async (): Promise<void> => {
    setIsLoading(true);
    setHasSearched(false);
    try {
      const targets = (
        unitsSelected.length > 0
          ? units.filter((u) => unitsSelected.includes(u.name ?? ""))
          : units
      ).filter((u) => !!u.id);

      if (targets.length === 0) {
        toast.warning("Nenhuma classe encontrada", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setReportData([]);
        return;
      }

      const result = await Promise.all(
        targets.map(async (unit) => {
          const people = await UnitService.getPeople(unit.id as string);
          return {
            unitId: unit.id as string,
            unitName: unit.name ?? "—",
            people: [...people].sort((a, b) =>
              a.name.localeCompare(b.name, "pt-BR")
            ),
          };
        })
      );

      result.sort((a, b) => a.unitName.localeCompare(b.unitName, "pt-BR"));

      if (result.every((u) => u.people.length === 0)) {
        toast.warning("Não há membros cadastrados nas classes selecionadas", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }

      setReportData(result);
    } catch (error: any) {
      toast.warning(error[0] ?? "Erro ao buscar os membros", {
        position: toast.POSITION.TOP_RIGHT,
      });
    } finally {
      setIsLoading(false);
      setHasSearched(true);
    }
  };

  const buildPDF = (): jsPDF => {
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(14);
    doc.setTextColor(52, 57, 165);
    doc.text("Listagem de Membros por Classe", 14, 18);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(
      `${reportData.length} classe(s) — ${totalMembers} membro(s)`,
      14,
      26
    );
    doc.text(`Gerado em: ${new Date().toLocaleString("pt-BR")}`, 14, 32);

    let cursorY = 40;

    reportData.forEach((unit) => {
      // Quebra de página quando não há espaço para o título + primeiras linhas
      if (cursorY > pageHeight - 40) {
        doc.addPage();
        cursorY = 20;
      }

      doc.setFontSize(11);
      doc.setTextColor(52, 57, 165);
      doc.text(`${unit.unitName} (${unit.people.length})`, 14, cursorY);

      if (unit.people.length === 0) {
        doc.setFontSize(9);
        doc.setTextColor(120);
        doc.text("Nenhum membro cadastrado.", 14, cursorY + 6);
        cursorY += 16;
        return;
      }

      autoTable(doc, {
        startY: cursorY + 4,
        head: [["#", "Nome", "Email", "Telefone", "Nascimento", "Batismo"]],
        body: unit.people.map((person, index) => [
          index + 1,
          person.name,
          person.email || "—",
          person.phone || "—",
          formatDate(person.date_of_birth),
          formatDate(person.date_baptism),
        ]),
        headStyles: {
          fillColor: [52, 57, 165],
          textColor: 255,
          fontStyle: "bold",
          fontSize: 9,
        },
        bodyStyles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 50 },
          4: { halign: "center" },
          5: { halign: "center" },
        },
        margin: { left: 14, right: 14 },
      });

      cursorY = ((doc as any).lastAutoTable?.finalY ?? cursorY) + 12;
    });

    return doc;
  };

  const exportPDF = () => {
    const doc = buildPDF();
    doc.save(
      `listagem-membros-${new Date().toISOString().slice(0, 10)}.pdf`
    );
  };

  const printPDF = () => {
    const doc = buildPDF();
    doc.autoPrint();
    const url = doc.output("bloburl") as unknown as string;
    const printWindow = window.open(url, "_blank");
    if (!printWindow) {
      toast.warning(
        "Não foi possível abrir a janela de impressão. Libere os pop-ups do site.",
        { position: toast.POSITION.TOP_RIGHT }
      );
    }
  };

  useEffect(() => {
    fetchUnits();
  }, []);

  return (
    <PageWrapper>
      <PageHeader onClick={() => navigate(-1)}>
        <FiChevronLeft />
        <span>Listagem de membros por classe</span>
      </PageHeader>

      <Content>
        <FiltersCard>
          <MultiSelect
            mt="md"
            withinPortal
            data={units.map((u) => u.name) as any}
            placeholder="Todas as classes"
            label="Classe(s)"
            classNames={classes}
            onChange={setUnitsSelected}
            disabled={isLoading}
          />

          <ActionsRow>
            <SearchButton onClick={fetchReport} disabled={isLoading}>
              {isLoading ? (
                <Spinner color="light" style={{ width: 18, height: 18 }} />
              ) : (
                <label style={{ cursor: "pointer" }}>Buscar</label>
              )}
            </SearchButton>

            {hasSearched && totalMembers > 0 && (
              <>
                <PrintButton onClick={printPDF}>
                  <FiPrinter size={16} />
                  <label style={{ cursor: "pointer" }}>Imprimir</label>
                </PrintButton>

                <ExportButton onClick={exportPDF}>
                  <FiDownload size={16} />
                  <label style={{ cursor: "pointer" }}>Exportar PDF</label>
                </ExportButton>
              </>
            )}
          </ActionsRow>
        </FiltersCard>

        {hasSearched && reportData.length > 0 && (
          <>
            <ReportLabel>
              {reportData.length} classe(s) — {totalMembers} membro(s)
            </ReportLabel>

            {reportData.map((unit) => (
              <UnitBlock key={unit.unitId}>
                <UnitBlockHeader>
                  <UnitBlockTitle>{unit.unitName}</UnitBlockTitle>
                  <UnitBlockCount>{unit.people.length} membro(s)</UnitBlockCount>
                </UnitBlockHeader>

                {unit.people.length === 0 ? (
                  <EmptyState>Nenhum membro cadastrado nesta classe.</EmptyState>
                ) : (
                  <>
                    {/* Tabela — tablet/desktop */}
                    <TableWrapper>
                      <StyledTable>
                        <thead>
                          <tr>
                            <Th center>#</Th>
                            <Th>Nome</Th>
                            <Th>Email</Th>
                            <Th>Telefone</Th>
                            <Th center>Nascimento</Th>
                            <Th center>Batismo</Th>
                          </tr>
                        </thead>
                        <tbody>
                          {unit.people.map((person, index) => (
                            <tr key={person.id}>
                              <Td center muted>
                                {index + 1}
                              </Td>
                              <Td>{person.name}</Td>
                              <Td>{person.email || "—"}</Td>
                              <Td>{person.phone || "—"}</Td>
                              <Td center>{formatDate(person.date_of_birth)}</Td>
                              <Td center>{formatDate(person.date_baptism)}</Td>
                            </tr>
                          ))}
                        </tbody>
                      </StyledTable>
                    </TableWrapper>

                    {/* Cards — mobile */}
                    <CardsWrapper>
                      {unit.people.map((person, index) => (
                        <PersonCard key={person.id}>
                          <PersonCardTitle>
                            {index + 1}. {person.name}
                          </PersonCardTitle>
                          <MetricRow>
                            <MetricLabel>Email</MetricLabel>
                            <MetricValue>{person.email || "—"}</MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Telefone</MetricLabel>
                            <MetricValue>{person.phone || "—"}</MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Nascimento</MetricLabel>
                            <MetricValue>
                              {formatDate(person.date_of_birth)}
                            </MetricValue>
                          </MetricRow>
                          <MetricRow>
                            <MetricLabel>Batismo</MetricLabel>
                            <MetricValue>
                              {formatDate(person.date_baptism)}
                            </MetricValue>
                          </MetricRow>
                        </PersonCard>
                      ))}
                    </CardsWrapper>
                  </>
                )}
              </UnitBlock>
            ))}
          </>
        )}

        {hasSearched && reportData.length === 0 && (
          <EmptyState>Nenhuma classe encontrada.</EmptyState>
        )}
      </Content>
    </PageWrapper>
  );
}

/* ─── Layout ─── */

const Content = styled.div`
  flex: 1;
  padding: 1.6rem 1.2rem;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;

  @media (min-width: 768px) {
    padding: 2.4rem 3.2rem;
    gap: 2rem;
  }
`;

const FiltersCard = styled.div`
  background: #fff;
  border: 1px solid #c0c0c9;
  border-radius: 0.8rem;
  padding: 1.6rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  @media (min-width: 768px) {
    padding: 1.6rem 2rem;
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
  margin-top: 0.8rem;

  @media (min-width: 768px) {
    flex-wrap: nowrap;
  }
`;

const SearchButton = styled.button`
  flex: 1;
  min-width: 120px;
  height: 3.5rem;
  border-radius: 0.4rem;
  background-color: #3439a5;
  border: 0.1rem solid #2b2fa3;
  display: flex;
  align-items: center;
  justify-content: center;

  label {
    color: #fff;
    font-size: 1.4rem;
    font-weight: bold;
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PrintButton = styled.button`
  flex: 1;
  min-width: 120px;
  height: 3.5rem;
  border-radius: 0.4rem;
  background-color: #fff;
  border: 0.1rem solid #3439a5;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: #3439a5;

  label {
    color: #3439a5;
    font-size: 1.4rem;
    font-weight: bold;
  }
`;

const ExportButton = styled.button`
  flex: 1;
  min-width: 120px;
  height: 3.5rem;
  border-radius: 0.4rem;
  background-color: #198754;
  border: 0.1rem solid #157347;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  color: #fff;

  label {
    color: #fff;
    font-size: 1.4rem;
    font-weight: bold;
  }
`;

const ReportLabel = styled.p`
  font-size: 1.3rem;
  color: #7a7a80;
  margin: 0;
  text-align: center;
`;

/* ─── Bloco por classe ─── */

const UnitBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const UnitBlockHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
`;

const UnitBlockTitle = styled.h3`
  font-size: 1.6rem;
  font-weight: 600;
  color: #3439a5;
  margin: 0;
`;

const UnitBlockCount = styled.span`
  font-size: 1.3rem;
  color: #7a7a80;
`;

/* ─── Tabela (tablet/desktop) ─── */

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid #c0c0c9;
  border-radius: 0.8rem;
  background: #fff;

  @media (max-width: 767px) {
    display: none;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 1.3rem;
`;

const Th = styled.th<{ center?: boolean }>`
  background: #3439a5;
  color: #fff;
  padding: 1rem 1.2rem;
  text-align: ${({ center }) => (center ? "center" : "left")};
  font-weight: 600;
  white-space: nowrap;
  font-size: 1.2rem;
`;

const Td = styled.td<{ center?: boolean; muted?: boolean }>`
  padding: 0.9rem 1.2rem;
  border-bottom: 1px solid #eee;
  text-align: ${({ center }) => (center ? "center" : "left")};
  color: ${({ muted }) => (muted ? "#aaa" : "#47474d")};

  tr:hover & {
    background: #f5f5fb;
  }
`;

/* ─── Cards (mobile) ─── */

const CardsWrapper = styled.div`
  display: none;
  flex-direction: column;
  gap: 1.2rem;

  @media (max-width: 767px) {
    display: flex;
  }
`;

const PersonCard = styled.div`
  background: #fff;
  border: 1px solid #c0c0c9;
  border-radius: 0.8rem;
  padding: 1.4rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const PersonCardTitle = styled.h4`
  font-size: 1.5rem;
  font-weight: 600;
  color: #3439a5;
  margin: 0 0 0.4rem 0;
  border-bottom: 1px solid #e0e0ee;
  padding-bottom: 0.6rem;
`;

const MetricRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.8rem;
`;

const MetricLabel = styled.span`
  font-size: 1.3rem;
  color: #7a7a80;
`;

const MetricValue = styled.span`
  font-size: 1.3rem;
  font-weight: 600;
  color: #47474d;
  text-align: right;
  word-break: break-word;
`;

/* ─── Estado vazio ─── */

const EmptyState = styled.p`
  text-align: center;
  color: #888;
  font-size: 1.4rem;
  padding: 2rem 0;
`;
